import { PropsWithChildren, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Paper, Box } from '@mui/material';
import { AppContext } from './all';
import PageTitle from '../layout/sc/PageTitle';
import { useOptionalHeaderSlot } from '../layout/HeaderContext';
import { useMenuSubtitle } from '../layout/sc/sidebar/useMenuSubtitle';

type SttPageProps = PropsWithChildren<{
    title?: string;
    innerTitle?: string;
    subtitle?: string | React.ReactNode;
    onBack?: () => void;
    help?: () => void;
}>;

export function SttPage({
    title,
    innerTitle,
    subtitle,
    onBack,
    help,
    children,
}: SttPageProps) {
    const cxt = useContext(AppContext)!;
    const headerSlot = useOptionalHeaderSlot();
    const menuSubtitle = useMenuSubtitle();

    /**
     * Init
     */
    useEffect(() => {
        if (title) {
            cxt.setHeader({
                title,
            });
        }
    }, []);

    // Determinar el título a mostrar
    const displayTitle = innerTitle || title || cxt?.header?.title;

    // Dentro del layout del gestor el título va a la cabecera compartida.
    // `innerTitle` se sigue pintando en el contenido: es su propósito, y lo usan
    // las páginas públicas, que no tienen cabecera compartida.
    const headerTarget = !innerTitle ? headerSlot?.slot : null;

    // Si la página no trae subtítulo se usa el del menú al que pertenece, como
    // en sporttia-competitions.
    const displaySubtitle = subtitle ?? menuSubtitle;

    const pageTitle = displayTitle ? (
        <PageTitle
            title={displayTitle}
            subtitle={displaySubtitle}
            onBack={onBack}
            help={help}
            compact={!!headerTarget}
        />
    ) : null;

    // Aviso a la cabecera de que esta página ya pinta su título, para que no lo
    // duplique con el suyo de reserva.
    const registerPageTitle = headerSlot?.setHasPageTitle;
    const rendersPortalTitle = !!headerTarget && !!pageTitle;

    useEffect(() => {
        if (!registerPageTitle || !rendersPortalTitle) return;

        registerPageTitle(true);
        return () => registerPageTitle(false);
    }, [registerPageTitle, rendersPortalTitle]);

    // Render
    return (
        <>
            {headerTarget && pageTitle && createPortal(pageTitle, headerTarget)}

            <Paper sx={{ overflow: 'hidden' }}>
                <Box p={3}>
                    {!headerTarget && pageTitle}

                    {children}
                </Box>
            </Paper>
        </>
    );
}
